import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { v4 as uuidV4 } from 'uuid';
import { NO_FILE } from 'components/FileUpload/paramsByState';
import { InlineNotification } from 'components/Notification/InlineNotification';
import { removeUploadedFileInfo, validateProductsRequestSaga } from 'domains/promotion/Promotion.store';
import {
  CFSectionLocal,
  CreatePromotionModel,
  ReferencesSectionLocal,
  UploadedReferencesFromFile,
} from 'domains/promotion/Promotion.types';
import { Box, Flex, MarginBox, Text } from 'UI';
import { useBreakpointSelectorFull } from 'utils';
import { STagReferencesError } from '../CreatePromotion.styled';

const REFERENCES_ALREADY_PROMOTED_DESCRIPTION_KEY =
  'backoffice.promotion.references.already_promoted.error_message.description';
const REFERENCES_ALREADY_PROMOTED_DESCRIPTION =
  'Several references already part of an existing promotion. Please adjust publication dates.';
const REFERENCES_ALREADY_PROMOTED_ACTION_HINT_KEY =
  'backoffice.promotion.references.already_promoted.error_message.action_hint';
const REFERENCES_ALREADY_PROMOTED_ACTION_HINT =
  'The user has to change the publication dates or delete the duplicate reference.';

export function containsAlreadyExistingCommercialFamilyCodes(commercialFamilyCodes?: CFSectionLocal[]) {
  return commercialFamilyCodes
    ? commercialFamilyCodes.some((section) => section.items.some((item) => item.removable && item.alreadyPromoted))
    : false;
}

export function containsAlreadyLocallyPromotedFamilyCodes(commercialFamilyCodes?: CFSectionLocal[]) {
  return commercialFamilyCodes
    ? commercialFamilyCodes.some((section) =>
        section.items.some((item) => item.removable && item.alreadyLocallyPromoted),
      )
    : false;
}

export function hasLocalCommercialFamiliesCodeDuplicates(code: string, commercialFamilyCodes?: CFSectionLocal[]) {
  if (!commercialFamilyCodes) {
    return false;
  }
  return !commercialFamilyCodes?.every((section) => {
    return !section.items.some((item) => item.item === code);
  });
}

export function containsAlreadyPromotedProduct(references?: ReferencesSectionLocal[]) {
  return (
    (references && references.length > 0 && references[0].items.length > 0 && references[0].items[0].alreadyPromoted) ??
    false
  );
}

export function containsAlreadyPromotedReferences(references?: ReferencesSectionLocal[]) {
  return references
    ? references.some((section) => section.items.some((item) => item.removable && item.alreadyPromoted))
    : false;
}

export function containsAlreadyExistingReferences(uploadedReferencesFromFiles: UploadedReferencesFromFile[]) {
  return uploadedReferencesFromFiles.some((d) => d.alreadyPromoted && d.alreadyPromoted.length > 0);
}

export function hasLocalReferenceDuplicates(code: string, references?: ReferencesSectionLocal[]) {
  if (!references) return false;
  return references.some((section) => section.items.find((item) => item.item === code));
}

export function containsAlreadyLocallyPromotedReferences(references?: ReferencesSectionLocal[]) {
  return references
    ? references.some((section) => section.items.some((item) => item.removable && item.alreadyLocallyPromoted))
    : false;
}

export function containsAlreadyLocallyPromotedReferencesInFiles(
  uploadedReferencesFromFiles: UploadedReferencesFromFile[],
) {
  return uploadedReferencesFromFiles.some((file) => (file.alreadyLocallyPromoted?.length ?? 0) > 0);
}

function getUploadedReferenceCodes(uploadedReferencesFromFiles: UploadedReferencesFromFile[]): Set<string> {
  return new Set(uploadedReferencesFromFiles.flatMap((file) => file.rows ?? []));
}

export function syncManualReferenceLocalDuplicates(
  references: ReferencesSectionLocal[] | undefined,
  uploadedReferencesFromFiles: UploadedReferencesFromFile[],
): ReferencesSectionLocal[] | undefined {
  if (!references) {
    return references;
  }

  const duplicateCounts = references
    .flatMap((section) => section.items)
    .reduce<Map<string, number>>((counts, item) => {
      counts.set(item.item, (counts.get(item.item) ?? 0) + 1);
      return counts;
    }, new Map());
  const uploadedReferenceCodes = getUploadedReferenceCodes(uploadedReferencesFromFiles);

  return references.map((section) => ({
    ...section,
    items: section.items.map((item) => ({
      ...item,
      alreadyLocallyPromoted: (duplicateCounts.get(item.item) ?? 0) > 1 || uploadedReferenceCodes.has(item.item),
    })),
  }));
}

export function syncUploadedReferenceLocalDuplicates(
  uploadedReferencesFromFiles: UploadedReferencesFromFile[],
  references: ReferencesSectionLocal[] | undefined,
): UploadedReferencesFromFile[] {
  const manualReferenceCodes = new Set(references?.flatMap((section) => section.items.map((item) => item.item)) ?? []);
  const uploadedReferenceCodesByFile = uploadedReferencesFromFiles.map((file) => new Set(file.rows ?? []));

  return uploadedReferencesFromFiles.map((file, index) => ({
    ...file,
    alreadyLocallyPromoted: [...uploadedReferenceCodesByFile[index]].filter(
      (reference) =>
        manualReferenceCodes.has(reference) ||
        uploadedReferenceCodesByFile.some((otherFile, i) => i !== index && otherFile.has(reference)),
    ),
  }));
}

export function shouldDisplayAlreadyPromotedReferencesNotification(
  references?: ReferencesSectionLocal[],
  uploadedReferencesFromFiles?: UploadedReferencesFromFile[],
): boolean {
  return (
    containsAlreadyPromotedReferences(references) ||
    containsAlreadyLocallyPromotedReferences(references) ||
    containsAlreadyLocallyPromotedReferencesInFiles(uploadedReferencesFromFiles ?? [])
  );
}

const OverlappingDatePromotionNotification = ({
  description,
  onChangeDates,
}: {
  description: string;
  onChangeDates?: () => void;
}) => {
  const { t } = useTranslation();

  return (
    <InlineNotification
      displayStyle={'error'}
      message={t(
        'backoffice.promotion.already_promoted.error_message.overlapping_date',
        'Overlapping date of existing promotion',
      )}
      description={description}
      action={
        onChangeDates && (
          <Flex
            onClick={() => {
              onChangeDates();
            }}
            justify={'flex-end'}
          >
            <Text
              type={'text'}
              displayStyle={'link'}
              decoration={'underline'}
              cursor={'pointer'}
              data-cy={'change-dates-link'}
            >
              {t('backoffice.promotion.already_promoted.change_dates', 'Change dates')}
            </Text>
          </Flex>
        )
      }
    />
  );
};

export const AlreadyPromotedReferencesNotification = ({
  promotion,
  setCurrentStep,
}: {
  promotion: CreatePromotionModel;
  setCurrentStep: (x: number) => void;
}) => {
  const { t } = useTranslation();

  if (
    !shouldDisplayAlreadyPromotedReferencesNotification(promotion.references, promotion.uploadedReferencesFromFiles)
  ) {
    return <></>;
  }

  const description = `${t(REFERENCES_ALREADY_PROMOTED_DESCRIPTION_KEY, REFERENCES_ALREADY_PROMOTED_DESCRIPTION, {
    count: 1,
  })} ${t(REFERENCES_ALREADY_PROMOTED_ACTION_HINT_KEY, REFERENCES_ALREADY_PROMOTED_ACTION_HINT)}`;

  return <OverlappingDatePromotionNotification description={description} onChangeDates={() => setCurrentStep(0)} />;
};

export const AlreadyExistingCommercialFamilyCodeNotification = ({
  promotion,
  setCurrentStep,
  setPromotion,
}: {
  promotion: CreatePromotionModel;
  setCurrentStep: (x: number) => void;
  setPromotion: (x: CreatePromotionModel) => void;
}) => {
  const { t } = useTranslation();

  if (promotion.promotionType === 'BANNER') {
    return <></>;
  }

  const handleAction = () => {
    setCurrentStep(0);
  };

  const handleDeleteAll = () => {
    const commercialFamilyCodes = promotion.commercialFamilyCodes;
    const notPromotedCodes: CFSectionLocal[] | undefined = commercialFamilyCodes?.map((section) => {
      return {
        ...section,
        items: section.items.filter(
          (item) => !(item.removable && (item.alreadyPromoted || item.alreadyLocallyPromoted)),
        ),
      };
    });
    setPromotion({ ...promotion, commercialFamilyCodes: notPromotedCodes });
  };

  return (
    <>
      {containsAlreadyExistingCommercialFamilyCodes(promotion.commercialFamilyCodes) ? (
        <OverlappingDatePromotionNotification
          description={`${t(
            'backoffice.promotion.commercial_family.already_promoted.error_message.description',
            'Commercial family already part of an existing promotion.',
          )} ${t(REFERENCES_ALREADY_PROMOTED_ACTION_HINT_KEY, REFERENCES_ALREADY_PROMOTED_ACTION_HINT)}`}
          onChangeDates={handleAction}
        />
      ) : (
        <>
          {containsAlreadyLocallyPromotedFamilyCodes(promotion.commercialFamilyCodes) && (
            <InlineNotification
              displayStyle={'error'}
              description={t(
                'backoffice.promotion.commercial_family.already_promoted.multiple_times.error_message',
                'Commercial family has been added multiple times.',
              )}
              action={
                <Flex onClick={handleDeleteAll} justify={'flex-end'}>
                  <Text type={'text'} displayStyle={'error'} decoration={'underline'} cursor={'pointer'}>
                    {t('common.action.delete_all', 'Delete all')}
                  </Text>
                </Flex>
              }
            />
          )}
        </>
      )}
    </>
  );
};

export const unblockRemovedReferenceInFirstFoundFile = (
  removedReference: string,
  indexToSearchFrom: number,
  updatedFiles: UploadedReferencesFromFile[],
) => {
  // if reference is removed, unblock this reference in first following file
  for (let i = indexToSearchFrom + 1; i < updatedFiles.length; i++) {
    const x = updatedFiles[i];
    const indexOfRemovedReference = x.alreadyLocallyPromoted?.indexOf(removedReference) ?? -1;
    if (indexOfRemovedReference > -1 && x.alreadyLocallyPromoted) {
      const newAlreadyLocallyPromoted = [...x.alreadyLocallyPromoted];
      newAlreadyLocallyPromoted.splice(indexOfRemovedReference, 1);
      updatedFiles[i] = {
        ...x,
        alreadyLocallyPromoted: newAlreadyLocallyPromoted,
        rows: [...(x.rows ?? []), removedReference],
      };
      return;
    }
  }
};

interface AlreadyExistingReferencesNotificationProps {
  promotion: CreatePromotionModel;
  setPromotion: (x: CreatePromotionModel) => void;
  duplicateItems: string[];
  fileId?: string;
  setCurrentStep: (x: number) => void;
}

export const AlreadyExistingReferencesNotification = ({
  promotion,
  setPromotion,
  duplicateItems,
  fileId,
  setCurrentStep,
}: AlreadyExistingReferencesNotificationProps) => {
  const { t } = useTranslation();
  const width = useBreakpointSelectorFull()({ xs: 750, sm: 750, md: 750, lg: 750, xl: 900, xml: 900, xxl: 1050 });
  const dispatch = useDispatch();

  const handleRemoveReference = (data: string[], index: number) => {
    const updatedFiles = [...promotion.uploadedReferencesFromFiles];
    const fileToUpdateIndex = updatedFiles.findIndex((file) => file.fileId === fileId);

    if (fileToUpdateIndex === -1) {
      return;
    }

    let fileToUpdate = updatedFiles[fileToUpdateIndex];
    const updatedReferences = [...data];
    const removedReference = updatedReferences[index];
    updatedReferences.splice(index, 1);
    const remainingRows = (fileToUpdate.rows ?? []).filter((reference) => reference !== removedReference);

    if (remainingRows.length === 0 && (fileToUpdate.alreadyPromoted?.length ?? 0) === 0) {
      fileToUpdate = {
        fileId: uuidV4(),
        rows: [],
        status: NO_FILE,
        discount: 0,
      };
    }

    updatedFiles[fileToUpdateIndex] = {
      ...fileToUpdate,
      rows: remainingRows,
      // locally
      alreadyPromoted: (fileToUpdate.alreadyPromoted ?? []).filter((r) => updatedReferences.includes(r)),
      alreadyLocallyPromoted: (fileToUpdate.alreadyLocallyPromoted ?? []).filter((r) => updatedReferences.includes(r)),
    };

    const synchronizedReferences = syncManualReferenceLocalDuplicates(promotion.references, updatedFiles);
    const synchronizedFiles = syncUploadedReferenceLocalDuplicates(updatedFiles, synchronizedReferences);

    const updatedPromotion = {
      ...promotion,
      references: synchronizedReferences,
      uploadedReferencesFromFiles: synchronizedFiles,
      duplicatePromotionIds: [],
    };
    setPromotion(updatedPromotion);
    dispatch(validateProductsRequestSaga({ promotion: updatedPromotion }));

    if (remainingRows.length === 0 && (fileToUpdate.alreadyPromoted?.length ?? 0) === 0 && fileId) {
      dispatch(removeUploadedFileInfo({ fileId }));
    }
  };

  const handleAction = () => {
    setCurrentStep(0);
  };

  if (!duplicateItems || duplicateItems.length === 0) {
    return <></>;
  }

  return (
    <>
      <Flex direction={'row'}>
        <Flex direction={'column'} size={8}>
          <Text type={'h5_bold'}>{t('promotion.creation.needs_your_attention', 'Need your attention')}</Text>
          <MarginBox mt={15} />
        </Flex>
        <MarginBox mr={60} />
      </Flex>
      <OverlappingDatePromotionNotification
        description={`${t(REFERENCES_ALREADY_PROMOTED_DESCRIPTION_KEY, REFERENCES_ALREADY_PROMOTED_DESCRIPTION, {
          count: duplicateItems.length,
        })} ${t(REFERENCES_ALREADY_PROMOTED_ACTION_HINT_KEY, REFERENCES_ALREADY_PROMOTED_ACTION_HINT)}`}
        onChangeDates={promotion.promotionType === 'PROMOTION_FLASH_QUANTITY_LIMITED' ? undefined : handleAction}
      />
      <Box width={width}>
        {duplicateItems.map((item, index) => {
          const handleRemoveItem = () => {
            if (index === undefined) {
              return;
            }
            handleRemoveReference(duplicateItems, index);
          };

          return (
            <React.Fragment key={`Reference_${item}`}>
              <STagReferencesError key={`Reference_${item}`} closable={true} onClose={() => handleRemoveItem()}>
                <Text type="sm" color="red" key={`Reference_${item}`}>
                  {item}
                </Text>
              </STagReferencesError>
            </React.Fragment>
          );
        })}
      </Box>
    </>
  );
};

export const AlreadyPromotedProductNotification = ({
  promotion,
  setCurrentStep,
}: {
  promotion: CreatePromotionModel;
  setCurrentStep: (x: number) => void;
}) => {
  const { t } = useTranslation();
  const handleAction = () => {
    setCurrentStep(0);
  };

  return (
    <>
      {containsAlreadyPromotedProduct(promotion.references) && (
        <OverlappingDatePromotionNotification
          description={`${t(REFERENCES_ALREADY_PROMOTED_DESCRIPTION_KEY, REFERENCES_ALREADY_PROMOTED_DESCRIPTION, {
            count: 1,
          })} ${t(REFERENCES_ALREADY_PROMOTED_ACTION_HINT_KEY, REFERENCES_ALREADY_PROMOTED_ACTION_HINT)}`}
          onChangeDates={handleAction}
        />
      )}
    </>
  );
};
